import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ClockIcon } from '@heroicons/react/24/outline';
import {
  CheckCircleIcon,
  XCircleIcon,
  PaperAirplaneIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/solid';

interface TimeEntryActivity {
  id: string;
  action: 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'RESUBMITTED';
  previousStatus: string | null;
  newStatus: string | null;
  remarks: string | null;
  actionRequired: string | null;
  createdAt: string;
  Actor: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

interface TimeEntryActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  activities: TimeEntryActivity[];
  isLoading?: boolean;
  entrySummary?: {
    workedDate: string;
    userName: string;
  };
}

const actionConfig = {
  SUBMITTED: {
    label: 'Submitted',
    icon: PaperAirplaneIcon,
    iconClass: 'text-blue-500',
    bgClass: 'bg-blue-100',
  },
  APPROVED: {
    label: 'Approved',
    icon: CheckCircleIcon,
    iconClass: 'text-green-500',
    bgClass: 'bg-green-100',
  },
  REJECTED: {
    label: 'Rejected',
    icon: XCircleIcon,
    iconClass: 'text-red-500',
    bgClass: 'bg-red-100',
  },
  RESUBMITTED: {
    label: 'Resubmitted',
    icon: ArrowPathIcon,
    iconClass: 'text-indigo-500',
    bgClass: 'bg-indigo-100',
  },
};

const TimeEntryActivityModal: React.FC<TimeEntryActivityModalProps> = ({
  isOpen,
  onClose,
  activities,
  isLoading = false,
  entrySummary,
}) => {
  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Strip HTML tags for plain text display
  const stripHtml = (html: string) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <ClockIcon className="h-6 w-6 text-indigo-600" />
                    </div>
                    <div>
                      <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                        Activity History
                      </Dialog.Title>
                      {entrySummary && (
                        <p className="text-sm text-gray-500 mt-1">
                          {entrySummary.userName} - {entrySummary.workedDate}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mt-4 ml-9">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full" />
                    </div>
                  ) : activities.length === 0 ? (
                    <div className="text-center py-8">
                      <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">No activity history found.</p>
                    </div>
                  ) : (
                    <div className="flow-root">
                      <ul className="-mb-8">
                        {activities.map((activity, index) => {
                          const config = actionConfig[activity.action] || actionConfig.SUBMITTED;
                          const Icon = config.icon;
                          const isLast = index === activities.length - 1;

                          return (
                            <li key={activity.id}>
                              <div className="relative pb-8">
                                {!isLast && (
                                  <span
                                    className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200"
                                    aria-hidden="true"
                                  />
                                )}
                                <div className="relative flex space-x-3">
                                  <div>
                                    <span
                                      className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${config.bgClass}`}
                                    >
                                      <Icon
                                        className={`h-5 w-5 ${config.iconClass}`}
                                        aria-hidden="true"
                                      />
                                    </span>
                                  </div>
                                  <div className="flex min-w-0 flex-1 justify-between space-x-4 pt-1.5">
                                    <div>
                                      <p className="text-sm text-gray-900">
                                        <span className="font-medium">
                                          {activity.Actor.firstName} {activity.Actor.lastName}
                                        </span>{' '}
                                        <span className={`font-medium ${config.iconClass}`}>
                                          {config.label.toLowerCase()}
                                        </span>{' '}
                                        the time entry
                                      </p>
                                      {activity.remarks && (
                                        <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                                          <p className="text-xs font-medium text-gray-500 mb-1">
                                            Remarks
                                          </p>
                                          <p className="text-sm text-gray-700">
                                            {stripHtml(activity.remarks)}
                                          </p>
                                        </div>
                                      )}
                                      {activity.actionRequired && (
                                        <div className="mt-2 p-3 bg-red-50 rounded-lg">
                                          <p className="text-xs font-medium text-red-600 mb-1">
                                            Action Required
                                          </p>
                                          <p className="text-sm text-red-700">
                                            {stripHtml(activity.actionRequired)}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                    <div className="whitespace-nowrap text-right text-xs text-gray-500">
                                      {formatDateTime(activity.createdAt)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default TimeEntryActivityModal;
